import React, { useState, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Toolbar, Typography, CircularProgress, TablePagination, Dialog,
    DialogTitle, DialogContent, DialogActions, Button, TextField, Chip, Box
} from "@mui/material";
import { getWalletTransactions, getWallet, getWalletTransaction } from "../services/walletService";
import {
    StyledTableContainer,
    StyledTableCell,
    StyledTableRow,
    StyledPaper,
} from "../assets/styles/tableStyle";
import { useParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";

const columns = [
    { label: "ID", accessor: "id" },
    { label: "Type", accessor: "transactionType" },
    { label: "Amount", accessor: "amount" },
    { label: "Status", accessor: "status" },
    { label: "Description", accessor: "description" },
    { label: "Created", accessor: "createdAt" },
    { label: "Approved By", accessor: "approvedBy" },
    { label: "Approved At", accessor: "approvedAt" }
];

const PAGE_SIZE = 10;

const statusColor = (status: string) => {
    switch (status) {
        case 'pending': return 'warning';
        case 'approved': return 'success';
        case 'rejected': return 'error';
        default: return 'default';
    }
};

function useQueryParams() {
    return React.useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const driverUid = params.get('driverUid');
        const riderUid = params.get('riderUid');
        return { driverUid, riderUid };
    }, [window.location.search]);
}

const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleString() : "-";

const getTypeColor = (type: string) => {
    switch (type) {
        // Income / Credits
        case 'credit':
        case 'ride_earning':
        case 'retention_bonus':
        case 'bonus_credit':
        case 'rider_subsidy':
            return 'success';

        // Expenses / Debits
        case 'debit':
        case 'ride_payment':
        case 'subscription_fee':
        case 'platform_charge_debit':
        case 'withdrawal':
            return 'error'; // Red

        default: return 'default';
    }
};

const WalletList: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [wallet, setWallet] = useState<any>(null);
    const [items, setItems] = useState<any[]>([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(PAGE_SIZE);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
    const [error, setError] = useState<string>("");
    const { driverUid, riderUid } = useQueryParams();

    // Fetch wallet summary
    useEffect(() => {
        // Pick the right UID and only call if one is present
        const filters =
            driverUid
                ? { driverUid }
                : riderUid
                ? { riderUid }
                : null;

        if (!filters) {
            setWallet(null);
            return;
        }

        getWallet(filters)
            .then(res => setWallet(res.data.data))
            .catch(() => setWallet(null));
    }, [driverUid, riderUid]);

    // Fetch transactions
    useEffect(() => {
        fetchTransactions(page, rowsPerPage);
    }, [page, rowsPerPage, driverUid, riderUid]);

    const fetchTransactions = async (pageNum: number, limit: number) => {
        setLoading(true);
        setError('');
        try {
            const res = await getWalletTransactions(pageNum + 1, limit, {
                ...(driverUid ? { driverUid } : {}),
                ...(riderUid ? { riderUid } : {}),
            });
            setItems(Array.isArray(res.data.data) ? res.data.data : []);
            setTotal(res.data.total || 0);
        } catch {
            setError("Unable to fetch wallet transactions.");
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (id: number) => {
        setDetailOpen(true);
        const res = await getWalletTransaction(id);
        setSelectedTransaction(res.data);
    };

    const handleCloseDetail = () => {
        setDetailOpen(false);
        setSelectedTransaction(null);
    };

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar>
                <Typography variant="h6">Wallet Transactions</Typography>
            </Toolbar>
            {/* Wallet Summary */}
            {wallet && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                        py: 2,
                        px: 2,
                        backgroundColor: "#f7f9fa",
                        borderRadius: 2,
                        minHeight: 60
                    }}
                >
                    <Chip label={`Available: ₹${wallet.availableBalance}`} color="success" sx={{ fontWeight: 600, fontSize: 16, px: 1.5 }} />
                    <Chip label={`Locked: ₹${wallet.lockedBalance}`} color="warning" sx={{ fontWeight: 600, fontSize: 16, px: 1.5 }} />
                    <Chip label={`Total: ₹${wallet.totalBalance}`} color="info" sx={{ fontWeight: 600, fontSize: 16, px: 1.5 }} />
                    <Chip label={`Currency: ${wallet.currency}`} sx={{ fontWeight: 500, fontSize: 16, px: 1.5 }} />
                </Box>

            )}
            {loading ? (
                <CircularProgress sx={{ margin: 2 }} />
            ) : (
                <StyledTableContainer>
                    <Table sx={{ minWidth: 1100 }}>
                        <TableHead>
                            <StyledTableRow>
                                {columns.map(col => (
                                    <StyledTableCell key={col.accessor}>{col.label}</StyledTableCell>
                                ))}
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(txn => (
                                <StyledTableRow
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    key={txn.id}
                                    onClick={() => handleRowClick(txn.id)}
                                >
                                    {columns.map(col => {
                                        if (col.accessor === "transactionType") {
                                            return (
                                                <StyledTableCell key={col.accessor}>
                                                    <Chip
                                                        label={txn.transactionType.replace(/_/g, ' ').toUpperCase()}
                                                        color={getTypeColor(txn.transactionType)}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </StyledTableCell>
                                            );
                                        }
                                        if (col.accessor === "status")
                                            return (
                                                <StyledTableCell key={col.accessor}>
                                                    <Chip label={txn.status} color={statusColor(txn.status)} size="small" />
                                                </StyledTableCell>
                                            );
                                        if (col.accessor === "description")
                                            return (
                                                <StyledTableCell key={col.accessor}>
                                                    <Tooltip title={txn.description || '-'}>
                            <span style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {txn.description || '-'}
                            </span>
                                                    </Tooltip>
                                                    {txn.transactionType === 'subscription_fee' && (
                                                        <Typography variant="caption" display="block" color="primary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                                            Plan: <strong>{txn.subscription?.plan?.name || "Unknown"}</strong>
                                                        </Typography>
                                                    )}
                                                </StyledTableCell>
                                            );
                                        if (["createdAt", "approvedAt"].includes(col.accessor))
                                            return (
                                                <StyledTableCell key={col.accessor}>
                                                    {formatDate(txn[col.accessor])}
                                                </StyledTableCell>
                                            );
                                        return (
                                            <StyledTableCell key={col.accessor}>
                                                {txn[col.accessor] ?? "-"}
                                            </StyledTableCell>
                                        );
                                    })}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </StyledTableContainer>
            )}
            {error && <div style={{ color: "red", margin: 12 }}>{error}</div>}
            {/* Transaction Details Dialog */}
            <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="md">
                <DialogTitle>Transaction Details</DialogTitle>
                <DialogContent>
                    {selectedTransaction ? (
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 2,
                            mt: 1,
                        }}>
                            {Object.keys(selectedTransaction).map((field) => (
                                <TextField
                                    key={field}
                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                    value={
                                        ['createdAt', 'approvedAt'].includes(field)
                                            ? formatDate(selectedTransaction[field])
                                            : (typeof selectedTransaction[field] === 'object' && selectedTransaction[field] !== null
                                            ? JSON.stringify(selectedTransaction[field], null, 2)
                                            : String(selectedTransaction[field] ?? "-"))
                                    }
                                    fullWidth
                                    margin="dense"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ readOnly: true }}
                                    multiline={typeof selectedTransaction[field] === 'object'}
                                />
                            ))}
                        </Box>
                    ) : <CircularProgress />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>Close</Button>
                </DialogActions>
            </Dialog>
        </StyledPaper>
    );
};

export default WalletList;
